import React from 'react';
import PropTypes from 'prop-types';

import { Box, Inline } from '@styleseat/ui';
import { colors } from '@styleseat/brand';
import useMount from '../../../hooks/useMount';
import { useJanitor } from '../../../hooks/useJanitor';
import useAsyncState from '../../../../../packages/hooks/src/useAsyncState';
import {
  BodyText,
  Button,
  HeaderText,
  Modal,
  Stack,
} from '../../ui';
import AvatarIcon from '../../AvatarIcon';
import AppointmentBadge from '../../consumer/search/AppointmentBadge';
import { getGeoIP } from '../../../modules/consumer/DeviceLocation';
import { ssFetchJSON } from '../../../modules/ssFetch';
import analytics from '../../../modules/analytics';

import topProsNational from './topNationalPros.json';

// Top Pros By Metro data file is in the 'styleseat-data-files' S3 bucket.
// https://s3.console.aws.amazon.com/s3/buckets/styleseat-data-files/
const TOP_PROS_BY_DMA_URL = 'https://d3qcyspl94612g.cloudfront.net/top_per_dma.json';

const VANITY_URL_PREFIX = 'https://styleseat.com/m/v/';
const AVATAR_URL_PREFIX = 'https://d220aniogakg8b.cloudfront.net/';
const AVATAR_URL_SUFFIX = '_90x90.jpg';

const VIEW_MODAL_EVENT = 'pro_learn_from_others_modal_viewed';
const VIEW_PROFILE_CLICK_EVENT = 'pro_learn_from_others_modal_view_profile_clicked';

const MAX_PROS_TO_DISPLAY = 6;

const LearnFromOthersModal = ({
  close,
}) => {
  const janitor = useJanitor();
  useMount(() => janitor.addPromise(analytics.track(VIEW_MODAL_EVENT)));

  const topPros = useAsyncState(async () => {
    const location = await getGeoIP().catch(() => ({}));
    let pros = [];

    // Try by metro first
    if (location.metro_area && location.metro_area !== '') {
      const prosGroup = await ssFetchJSON(TOP_PROS_BY_DMA_URL).catch(() => ({}));
      pros = prosGroup[location.metro_area] || [];
    }

    // Fallback to national set if unable to find by metro
    if (pros.length === 0) {
      pros = topProsNational;
    }

    return Object.keys(pros).sort().slice(0, MAX_PROS_TO_DISPLAY).map(rank => pros[rank]);
  }, []);

  const handleViewProfileClick = (id, vanitySlug) => {
    const url = `${VANITY_URL_PREFIX}${vanitySlug}`;
    janitor.addPromise(analytics.track(VIEW_PROFILE_CLICK_EVENT, { provider_id: id, url }));
    window.open(url, '_blank');
  };

  if (!topPros) {
    return null;
  }

  return (
    <Modal
      testID="learn-from-others-modal"
      appearance="fullScreen"
      showClose
      onCloseRequest={close}
      renderTitle={() => (
        <Box style={{ marginLeft: 'auto', marginRight: 'auto' }} alignItems="center">
          <Box style={{ width: '70%' }}>
            <Stack style={{ width: '100%' }} paddingTop={8} justifyContent="space-around" alignItems="center">
              <HeaderText level="2" textAlign="center">Get inspiration</HeaderText>
              <BodyText textAlign="center">See how similar professionals build their profile</BodyText>
            </Stack>
          </Box>
        </Box>
      )}
    >
      <Stack
        paddingTop={8}
        borderBottomWidth={1}
        borderColor={colors.blackInBlack}
        borderStyle="solid"
      >
        {topPros.map(({
          provider_id,
          provider_name,
          image_path,
          vanity_url,
          booking_badge,
        }) => (
          <Box
            key={provider_id}
            borderTopWidth={1}
            borderColor={colors.blackInBlack}
            borderStyle="solid"
          >
            <Inline space={8} padding={8}>
              <Box flexDirection="row" alignItems="center">
                <AvatarIcon
                  icon={`${AVATAR_URL_PREFIX}${image_path}${AVATAR_URL_SUFFIX}`}
                  name={provider_name}
                  sizeClass="extra-small"
                />
              </Box>
              <Stack
                alignItems="flex-start"
                paddingTop={8}
                paddingBottom={8}
              >
                <HeaderText level={4}>
                  {provider_name}
                </HeaderText>
                {!!booking_badge && (
                  <AppointmentBadge
                    proJoinDate="2016-01-01"
                    totalAppointments={parseInt(booking_badge, 10)}
                  />
                )}
              </Stack>
              <Box
                alignItems="flex-end"
                style={{
                  marginTop: 'auto',
                  marginBottom: 'auto',
                  marginLeft: 'auto',
                  marginRight: 4,
                }}
              >
                <Button
                  testID={`learn-from-others-view-profile-pro${provider_id}`}
                  onClick={() => handleViewProfileClick(provider_id, vanity_url)}
                  size="medium"
                  appearance="text-light"
                  style={{
                    textDecoration: 'none',
                    lineHeight: '28px',
                    paddingRight: 10,
                    paddingLeft: 10,
                  }}
                >
                  View Profile
                </Button>
              </Box>
            </Inline>
          </Box>
        ))}
      </Stack>
    </Modal>
  );
};

LearnFromOthersModal.propTypes = {
  close: PropTypes.func,
};

LearnFromOthersModal.defaultProps = {
  close: () => { },
};

export default LearnFromOthersModal;
